'use client'

import React, { useEffect, useRef } from 'react'

interface BatteryAnimationProps {
  className?: string
  imageUrl?: string
  width?: number
  height?: number
  particleGap?: number
  particleSize?: number
  noise?: number
  mouseForce?: number
  gravity?: number
}

export const BatteryAnimation: React.FC<BatteryAnimationProps> = ({
  className = '',
  imageUrl = '/battery-icon.svg',
  width = 600,
  height = 498,
  particleGap = 4,
  particleSize = 1,
  noise = 5,
  mouseForce = 30,
  gravity = 0.08,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const animationRef = useRef<number | undefined>(undefined)
  const particlesRef = useRef<any[]>([])
  const originsRef = useRef<any[]>([])
  const touchesRef = useRef<any[]>([])
  const imageRef = useRef<HTMLImageElement | undefined>(undefined)
  const stateRef = useRef<'running' | 'stopped'>('stopped')

  useEffect(() => {
    const canvas = canvasRef.current
    const container = containerRef.current
    if (!canvas || !container) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    // Load image
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.src = imageUrl

    img.onload = () => {
      imageRef.current = img
      const imageWidth = img.naturalWidth || img.width
      const imageHeight = img.naturalHeight || img.height
      const imageRatio = imageWidth / imageHeight

      // Set canvas size
      canvas.width = width
      canvas.height = height

      // Calculate render size
      const renderWidth = Math.min(width, imageWidth)
      const renderHeight = Math.floor(renderWidth / imageRatio)
      const offsetX = Math.floor((width - renderWidth) / 2)
      const offsetY = Math.floor((height - renderHeight) / 2)

      // Create temporary canvas to get image data
      const tempCanvas = document.createElement('canvas')
      tempCanvas.width = renderWidth
      tempCanvas.height = renderHeight
      const tempCtx = tempCanvas.getContext('2d')
      if (!tempCtx) return

      tempCtx.drawImage(img, 0, 0, renderWidth, renderHeight)
      const imageData = tempCtx.getImageData(0, 0, renderWidth, renderHeight).data

      // Initialize origins (target positions for particles)
      const origins: any[] = []
      for (let x = 0; x < renderWidth; x += particleGap) {
        for (let y = 0; y < renderHeight; y += particleGap) {
          const i = 4 * (x + y * renderWidth)
          const r = imageData[i] ?? 0
          const g = imageData[i + 1] ?? 0
          const b = imageData[i + 2] ?? 0
          const alpha = imageData[i + 3] ?? 0

          if (alpha > 0) {
            origins.push({
              x: offsetX + x,
              y: offsetY + y,
              z: 50,
              color: [255 - r, 255 - g, 255 - b, alpha], // Invert RGB to make black->white
            })
          }
        }
      }

      originsRef.current = origins

      // Initialize particles with random positions near their targets
      const particles: any[] = []
      origins.forEach((origin) => {
        particles.push({
          x: origin.x + (Math.random() - 0.5) * 100,
          y: origin.y + (Math.random() - 0.5) * 100,
          z: 0,
          vx: 0,
          vy: 0,
          vz: 0,
          isHidden: false,
        })
      })

      particlesRef.current = particles

      const speed = Math.log(origins.length) / 10
      const gravityFactor = 1 - gravity * speed

      // Animation loop
      const animate = () => {
        if (stateRef.current !== 'running') return

        // Calculate particle physics
        particles.forEach((particle, idx) => {
          const origin = origins[idx]

          // Calculate force towards origin
          const dx = origin.x - particle.x + (Math.random() - 0.5) * noise
          const dy = origin.y - particle.y + (Math.random() - 0.5) * noise
          const dz = origin.z - particle.z + ((Math.random() - 0.5) * noise) / 1000

          const distance = Math.sqrt(dx * dx + dy * dy + dz * dz)
          const force = 0.012 * distance

          particle.vx += force * (dx / distance) * speed
          particle.vy += force * (dy / distance) * speed
          particle.vz += force * (dz / distance) * speed

          // Apply mouse/touch forces
          touchesRef.current.forEach((touch) => {
            const tdx = particle.x - touch.x
            const tdy = particle.y - touch.y
            const tdz = particle.z - touch.z
            const tdist = Math.sqrt(tdx * tdx + tdy * tdy + tdz * tdz)

            if (tdist > 0) {
              const tforce = (mouseForce * touch.force) / tdist

              particle.vx += tforce * (tdx / tdist) * speed
              particle.vy += tforce * (tdy / tdist) * speed
              particle.vz += tforce * (tdz / tdist) * speed
            }
          })

          // Apply gravity/friction with smoother damping
          particle.vx *= gravityFactor * 0.985
          particle.vy *= gravityFactor * 0.985
          particle.vz *= gravityFactor * 0.985

          // Update positions
          particle.x += particle.vx
          particle.y += particle.vy
          particle.z += particle.vz

          // Check bounds
          particle.isHidden =
            particle.x < 0 || particle.x >= width || particle.y < 0 || particle.y >= height
        })

        // Draw particles
        const imageData = ctx.createImageData(width, height)

        particles.forEach((particle, idx) => {
          if (particle.isHidden) return

          const origin = origins[idx]
          const px = Math.floor(particle.x)
          const py = Math.floor(particle.y)

          if (px >= 0 && px < width && py >= 0 && py < height) {
            const i = 4 * (px + py * width)
            imageData.data[i + 0] = origin.color[0]
            imageData.data[i + 1] = origin.color[1]
            imageData.data[i + 2] = origin.color[2]
            imageData.data[i + 3] = origin.color[3]
          }
        })

        ctx.putImageData(imageData, 0, 0)

        animationRef.current = requestAnimationFrame(animate)
      }

      // Mouse/touch handlers
      const handleMouseMove = (e: MouseEvent) => {
        const rect = canvas.getBoundingClientRect()
        const scaleX = width / rect.width
        const scaleY = height / rect.height
        touchesRef.current = [
          {
            x: (e.clientX - rect.left) * scaleX,
            y: (e.clientY - rect.top) * scaleY,
            z: 50,
            force: 1,
          },
        ]
      }

      const handleMouseOut = () => {
        touchesRef.current = []
      }

      const handleTouchMove = (e: TouchEvent) => {
        e.preventDefault()
        const rect = canvas.getBoundingClientRect()
        const scaleX = width / rect.width
        const scaleY = height / rect.height
        touchesRef.current = Array.from(e.touches).map((touch) => ({
          x: (touch.clientX - rect.left) * scaleX,
          y: (touch.clientY - rect.top) * scaleY,
          z: 50,
          force: 1,
        }))
      }

      const handleTouchEnd = () => {
        touchesRef.current = []
      }

      // Add event listeners
      canvas.addEventListener('mousemove', handleMouseMove)
      canvas.addEventListener('mouseout', handleMouseOut)
      canvas.addEventListener('touchmove', handleTouchMove, { passive: false })
      canvas.addEventListener('touchend', handleTouchEnd)

      // Start animation
      stateRef.current = 'running'
      animate()

      // Cleanup
      return () => {
        stateRef.current = 'stopped'
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current)
        }
        canvas.removeEventListener('mousemove', handleMouseMove)
        canvas.removeEventListener('mouseout', handleMouseOut)
        canvas.removeEventListener('touchmove', handleTouchMove)
        canvas.removeEventListener('touchend', handleTouchEnd)
      }
    }

    img.onerror = () => {
      console.error('Failed to load battery image:', imageUrl)
    }
  }, [imageUrl, width, height, particleGap, particleSize, noise, mouseForce, gravity])

  return (
    <div ref={containerRef} className={`overflow-visible ${className}`}>
      <canvas
        ref={canvasRef}
        style={{
          width: '100%',
          height: 'auto',
          maxWidth: `${width}px`,
          overflow: 'visible',
        }}
      />
    </div>
  )
}
